import { useTranslation } from 'react-i18next';
import type { SamAction } from '@/components/SAM/samActionTypes';
import {
  ActionBoltIcon,
  AlertCircleIcon,
  BoxIcon,
  CheckCircleIcon,
  CheckIcon,
  FieldDotIcon,
  PinIcon,
  SpinnerIcon,
  XCircleIcon,
} from '@/components/chat/ChatIcons';

export interface ConfirmMessage {
  kind: 'confirm';
  id: string;
  action: SamAction;
  /** Not set by confirmation cards today — kept optional so this type structurally matches the
   *  other workspace-surface messages (see ChatPage's turn-matched reply-text lookup). */
  messageId?: string;
  resolved?: 'approved' | 'rejected';
  /** The tool's actual outcome once it has run — arrives separately from `resolved` (which only
   *  reflects the user's click). Undefined while an approved action is still executing. */
  outcome?: 'success' | 'failed' | 'cancelled';
  outcomeMessage?: string;
  outcomeData?: Record<string, unknown> | null;
}

/** Backend sends titles like "Confirm — add new property"; strip the prefix and sentence-case
 *  the remainder so the card reads as a clean action label ("Add new property"). */
const formatTitle = (raw: string | undefined, toolName: string | undefined, fallback: string): string => {
  const src = (raw || toolName || '').trim();
  if (!src) return fallback;
  const stripped = src.replace(/^\s*confirm\s*[—:-]\s*/i, '').replace(/_/g, ' ').trim();
  if (!stripped) return fallback;
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
};

const formatKey = (k: string) =>
  k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** A tool arg's value is usually a primitive, but a free-form dict arg (e.g. update_engagement's
 *  `updates`) arrives as a nested object — `String(v)` on that yields the literal "[object
 *  Object]" (JS's default, no-op Object.prototype.toString), not the field's actual content.
 *  Render it instead as its own "key: value" pairs so the card still shows what's actually being
 *  changed. */
const stringifyValue = (v: unknown): string => {
  if (Array.isArray(v)) return v.map(stringifyValue).join(', ');
  if (v !== null && typeof v === 'object') {
    return Object.entries(v as Record<string, unknown>)
      .map(([k, val]) => `${formatKey(k)}: ${stringifyValue(val)}`)
      .join(', ');
  }
  return String(v);
};

/** Per-field glyph, guessed from the arg's key name — a property/address value gets a pin, a
 *  type/category value gets a box, anything else falls back to a plain list-line glyph. This
 *  stays generic across tools (create_engagement, create_contact, etc.) rather than hardcoding
 *  one fixed set of fields. */
const isPropertyField = (key: string) => /propert|address/i.test(key);
const isTypeField = (key: string) => /type/i.test(key);
const fieldIconFor = (key: string) => {
  if (isPropertyField(key)) return PinIcon;
  if (isTypeField(key)) return BoxIcon;
  return FieldDotIcon;
};

/** Resolved outcome state, folding msg.resolved + msg.outcome into one thing to render — the
 *  card's footer swaps between the Cancel/Confirm buttons and exactly one of these once the
 *  user has decided. */
type ResolvedState = 'cancelled' | 'running' | 'success' | 'failed';

/** ResolvedState value -> i18n key under chat.confirm.* (the 'success' state reads as "Done"). */
const STATE_LABEL_KEY: Record<ResolvedState, string> = {
  cancelled: 'chat.confirm.cancelled',
  running: 'chat.confirm.running',
  success: 'chat.confirm.done',
  failed: 'chat.confirm.failed',
};

const resolveState = (msg: ConfirmMessage): ResolvedState | null => {
  if (!msg.resolved) return null;
  if (msg.resolved === 'rejected') return 'cancelled';
  if (!msg.outcome) return 'running';
  if (msg.outcome === 'failed') return 'failed';
  if (msg.outcome === 'cancelled') return 'cancelled';
  return 'success';
};

/** Renders full-width in the workspace panel (see ChatPage's surfacePanel) as a self-contained
 *  card — never a chat bubble. The panel already provides the scroll container + padding. */
export const ConfirmCard = ({
  msg,
  onDecide,
}: {
  msg: ConfirmMessage;
  onDecide: (d: 'approved' | 'rejected') => void;
}) => {
  const { t } = useTranslation();
  const a = msg.action;
  const args = a.tool_args || {};
  const title = formatTitle(a.title, a.tool_name, t('chat.confirm.defaultTitle'));
  const entries = Object.entries(args).filter(
    ([, v]) => v !== null && v !== undefined && stringifyValue(v).trim() !== '',
  );
  const state = resolveState(msg);

  return (
    <div className="mx-auto w-full max-w-md overflow-hidden rounded-2xl border border-ui-border bg-ui-card shadow-sm">
      <div className="flex items-start gap-3.5 px-5 pb-4 pt-5">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px] bg-brand-dark/10 text-brand-dark">
          <ActionBoltIcon className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h1 className="text-[15px] font-semibold leading-tight tracking-tight text-ui-text">{title}</h1>
          <p className="mt-0.5 text-[13px] leading-snug text-ui-text2">{t('chat.confirm.subtitle')}</p>
        </div>
      </div>

      {entries.length > 0 && (
        <div className="mx-5 max-h-[45vh] overflow-y-auto border-t border-ui-border">
          <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-x-4">
            {entries.map(([k, v], i) => {
              const Icon = fieldIconFor(k);
              const value = stringifyValue(v);
              const [main, ...rest] = isPropertyField(k) ? value.split(',') : [value];
              const sub = rest.join(',').trim();
              // On the sm:grid-cols-2 layout, a row is fully "last" once both its slots (or its
              // lone trailing slot, for an odd count) are past the second-to-last row boundary —
              // nth-last-child(-n+2) instead picks the last 2 DOM nodes regardless of row
              // alignment, which mismatches the visual last row when entries.length is odd.
              const isLastVisualRow = i >= entries.length - (entries.length % 2 === 0 ? 2 : 1);
              return (
                <div
                  key={k}
                  className={`flex items-start gap-3 border-b border-ui-border py-3.5 last:border-b-0 ${isLastVisualRow ? 'sm:border-b-0' : ''}`}
                >
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-ui-subtle text-ui-text2">
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-ui-text3">
                      {formatKey(k)}
                    </div>
                    {isTypeField(k) ? (
                      <span className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-brand-dark/10 px-2.5 py-1 text-[13px] font-semibold text-brand-dark">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-dark" />
                        {value}
                      </span>
                    ) : (
                      <>
                        <div className="mt-0.5 break-words text-[14.5px] font-medium leading-snug text-ui-text">
                          {main.trim()}
                        </div>
                        {sub && <div className="mt-0.5 text-xs text-ui-text3">{sub}</div>}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {state ? (
        <div className="mx-5 mb-5 mt-4 flex items-start gap-2.5 rounded-xl bg-ui-subtle p-3.5">
          {state === 'cancelled' && <XCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-ui-text3" />}
          {state === 'running' && <SpinnerIcon className="mt-0.5 h-5 w-5 shrink-0 text-ui-text2" />}
          {state === 'success' && <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-success" />}
          {state === 'failed' && <AlertCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-danger" />}
          <div className="min-w-0">
            <p
              className={`text-sm font-medium leading-tight ${
                state === 'success'
                  ? 'text-success'
                  : state === 'failed'
                    ? 'text-danger'
                    : 'text-ui-text2'
              }`}
            >
              {t(STATE_LABEL_KEY[state])}
            </p>
            {state !== 'running' && msg.outcomeMessage && (
              <p className="mt-1 text-xs leading-snug text-ui-text3">{msg.outcomeMessage}</p>
            )}
          </div>
        </div>
      ) : (
        <div className="flex gap-2.5 p-5">
          <button
            type="button"
            onClick={() => onDecide('rejected')}
            className="flex-1 rounded-[11px] border border-ui-border bg-ui-card px-4 py-3 text-sm font-semibold text-ui-text transition hover:bg-ui-hover active:scale-[0.98]"
          >
            {t('chat.confirm.cancel')}
          </button>
          <button
            type="button"
            onClick={() => onDecide('approved')}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-[11px] bg-brand-dark px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-dark/90 active:scale-[0.98]"
          >
            <CheckIcon className="h-3.5 w-3.5" />
            {t('chat.confirm.confirm')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ConfirmCard;
